import { createRootRouteWithContext, Outlet } from "@tanstack/react-router";
// As fontes entram pelo JS: importadas no CSS, o Tailwind v4 inlina o @import sem
// reescrever os url(./files/...) da @fontsource e o build não emite os arquivos.
import "@fontsource/poppins/400.css";
import "@fontsource/poppins/500.css";
import "@fontsource/poppins/600.css";
import "@fontsource/poppins/700.css";
import "@/styles/index.css";
import "@/styles/calendar.css";
import "@/styles/chatbot-zendesk.css";
import "@/styles/rich-text.css";
import { useEffect, useState, type JSX } from "react";
import { z } from "zod";

import { AllocatedRoomBarController } from "@/components/room/allocated/allocated-room-bar-controller";
import { UserFeedbackButton } from "@/components/ui/button/user-feedback-button";
import { DisconnectedErrorDrawer } from "@/components/ui/drawer/disconnected-error-drawer";
import { ErrorDrawer } from "@/components/ui/drawer/error-drawer";
import { UnsavedChangesDrawer } from "@/components/ui/drawer/unsaved-changes-drawer";
import { MainElement } from "@/components/ui/layout/page/main-element";
import { useIdentifyUser } from "@/hooks/use-identify-user";
import { useNetworkStatus } from "@/hooks/use-network-status";
import { usePushNotificationPrompt } from "@/hooks/use-push-notification-prompt";
import { useSetMaxZoom } from "@/hooks/use-set-max-zoom";
import { useUserPilotInitializer } from "@/hooks/use-user-pilot-initializer";
import { ReturnRoomActionSearchParamsSchema } from "@/lib/action-schemas/return-room";
import { ErrorDrawerProvider } from "@/lib/context/error-drawer-context";
import { UnsavedChangesDrawerProvider } from "@/lib/context/unsaved-changes-drawer-context";
import { rootBeforeLoad } from "@/routes/-root/root-before-load";
import type { RootRouteContext } from "@/routes/-root/-root-context";

const GenericActionSearchParams = z.object({
  action: z.string().optional(),
});

const actionSearchParamsSchema = z.union([
  GenericActionSearchParams,
  ReturnRoomActionSearchParamsSchema,
]);

// biome-ignore lint/plugin/route-file-exports: predates the rule; move it out
export const RootRoute = (): JSX.Element => {
  useIdentifyUser();
  usePushNotificationPrompt();
  useUserPilotInitializer();
  useSetMaxZoom();

  const isConnected = useNetworkStatus();
  const [showDisconnectedError, setShowDisconnectedError] = useState(false);
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);

  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;
    if (!isConnected) {
      setShowDisconnectedError(true);
      setIsDrawerVisible(true);
    } else if (showDisconnectedError) {
      timer = setTimeout(() => {
        setShowDisconnectedError(false);
        setIsDrawerVisible(false);
      }, 3000);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [isConnected, showDisconnectedError]);

  const disconnectedErrorDrawerDismiss = (value: boolean): void => {
    if (!value) {
      setShowDisconnectedError(false);
      setIsDrawerVisible(false);
    }
  };

  return (
    <MainElement>
      <ErrorDrawerProvider>
        <UnsavedChangesDrawerProvider>
          <Outlet />
          <AllocatedRoomBarController />
          <UserFeedbackButton />
          <ErrorDrawer />
          <UnsavedChangesDrawer />
          <DisconnectedErrorDrawer
            setOpen={disconnectedErrorDrawerDismiss}
            open={isDrawerVisible}
          />
        </UnsavedChangesDrawerProvider>
      </ErrorDrawerProvider>
    </MainElement>
  );
};

export const Route = createRootRouteWithContext<RootRouteContext>()({
  component: RootRoute,
  beforeLoad: async ({ context, location }) => {
    await rootBeforeLoad(context, location);
    return { user: context.user };
  },
  validateSearch: (search: Record<string, unknown>): Record<string, unknown> => {
    const parsed = actionSearchParamsSchema.safeParse(search);
    return parsed.success ? { ...search, ...parsed.data } : search;
  },
});
